import React, { useState, useEffect } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import { useAlert } from "../../contexts/AlertContext";
import { adminAPI } from "../../services/api";
import { WalletAddress } from "../../types";
import {
  MdAccountBalanceWallet,
  MdAdd,
  MdEdit,
  MdDelete,
  MdRefresh,
  MdContentCopy,
  MdCheckCircle,
} from "react-icons/md";
import getDocumentUrl from "../../utils/getDocumentUrl";

const WalletManagement = () => {
  const { addAlert } = useAlert();
  const [walletAddresses, setWalletAddresses] = useState<WalletAddress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWallet, setEditingWallet] = useState<WalletAddress | null>(
    null
  );
  const [copiedAddress, setCopiedAddress] = useState("");
  const [formData, setFormData] = useState({
    coin: "bitcoin",
    network: "bitcoin",
    address: "",
    qr_code: null as File | null,
    is_active: true,
  });
  const [qrCodePreview, setQrCodePreview] = useState("");

  const WalletIcon = MdAccountBalanceWallet as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const AddIcon = MdAdd as React.FC<React.SVGProps<SVGSVGElement>>;
  const EditIcon = MdEdit as React.FC<React.SVGProps<SVGSVGElement>>;
  const DeleteIcon = MdDelete as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const CopyIcon = MdContentCopy as React.FC<React.SVGProps<SVGSVGElement>>;
  const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;

  const coins = [
    { id: "bitcoin", name: "Bitcoin", networks: ["bitcoin"] },
    { id: "ethereum", name: "Ethereum", networks: ["erc20"] },
    {
      id: "tether",
      name: "Tether (USDT)",
      networks: ["erc20", "bep20", "trc20"],
    },
    { id: "solana", name: "Solana", networks: ["solana"] },
    { id: "ripple", name: "Ripple (XRP)", networks: ["ripple"] },
    // Add BNB and XLM
    { id: "binancecoin", name: "BNB", networks: ["bep20", "bep2"] },
    { id: "stellar", name: "Stellar (XLM)", networks: ["stellar"] },
  ];

  const fetchWalletAddresses = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getWalletAddresses();
      setWalletAddresses(response.data);
    } catch (error: any) {
      addAlert({ type: "error", message: "Failed to fetch wallet addresses" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletAddresses();
    // eslint-disable-next-line
  }, []);

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    addAlert({ type: "success", message: "Address copied to clipboard!" });
    setTimeout(() => setCopiedAddress(""), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const submitData = new FormData();
      submitData.append("coin", formData.coin);
      submitData.append("network", formData.network);
      submitData.append("address", formData.address);
      submitData.append("is_active", formData.is_active ? "1" : "0");

      if (formData.qr_code) {
        submitData.append("qr_code", formData.qr_code);
      }

      if (editingWallet) {
        await adminAPI.updateWalletAddress(editingWallet.id, submitData);
        addAlert({
          type: "success",
          message: "Wallet address updated successfully",
        });
      } else {
        await adminAPI.createWalletAddress(submitData);
        addAlert({
          type: "success",
          message: "Wallet address created successfully",
        });
      }

      setIsModalOpen(false);
      setEditingWallet(null);
      setFormData({
        coin: "bitcoin",
        network: "bitcoin",
        address: "",
        qr_code: null,
        is_active: true,
      });
      setQrCodePreview("");
      fetchWalletAddresses();
    } catch (error: any) {
      console.error("Error submitting wallet:", error);
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to save wallet address",
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !window.confirm("Are you sure you want to delete this wallet address?")
    ) {
      return;
    }

    try {
      await adminAPI.deleteWalletAddress(id);
      addAlert({
        type: "success",
        message: "Wallet address deleted successfully",
      });
      fetchWalletAddresses();
    } catch (error: any) {
      addAlert({
        type: "error",
        message:
          error.response?.data?.error || "Failed to delete wallet address",
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, qr_code: file }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setQrCodePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const openCreateModal = () => {
    setEditingWallet(null);
    setFormData({
      coin: "bitcoin",
      network: "bitcoin",
      address: "",
      qr_code: null,
      is_active: true,
    });
    setQrCodePreview("");
    setIsModalOpen(true);
  };

  const openEditModal = (wallet: WalletAddress) => {
    setEditingWallet(wallet);
    setFormData({
      coin: wallet.coin,
      network: wallet.network,
      address: wallet.address,
      qr_code: null,
      is_active: wallet.is_active,
    });
    if (wallet.qr_code) {
      setQrCodePreview(getDocumentUrl(wallet.qr_code) || "");
    }
    setIsModalOpen(true);
  };

  const getCoinName = (coinId: string) => {
    return coins.find((coin) => coin.id === coinId)?.name || coinId;
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full dark:bg-green-900 dark:text-green-200">
        Active
      </span>
    ) : (
      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full dark:bg-red-900 dark:text-red-200">
        Inactive
      </span>
    );
  };

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
          >
            Wallet Management
          </h1>
          <p className={`${CommonClasses.textGray}`}>
            Manage cryptocurrency wallet addresses for user deposits.
          </p>
        </div>
        <div
          className={`${CommonClasses.flexStart} gap-2 max-sm:w-full max-sm:justify-between`}
        >
          <button
            onClick={fetchWalletAddresses}
            disabled={isLoading}
            className={`${GlobalStyles.buttonIconOutline} ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <RefreshIcon
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
          <button
            onClick={openCreateModal}
            className={`${GlobalStyles.buttonPrimary} !text-sm !w-28`}
          >
            <AddIcon className="w-4 h-4 mr-2" />
            Add
          </button>
        </div>
      </div>

      {/* Wallet Addresses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-3 text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className={`${CommonClasses.textGray} mt-4`}>
              Loading wallet addresses...
            </p>
          </div>
        ) : walletAddresses.length === 0 ? (
          <div className="col-span-3 text-center py-8">
            <WalletIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className={`${CommonClasses.textGray} mb-2`}>
              No wallet addresses configured
            </p>
            <p className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}>
              Add your first wallet address to start accepting deposits
            </p>
          </div>
        ) : (
          walletAddresses.map((wallet) => (
            <div key={wallet.id} className={`${GlobalStyles.sectionWrap}`}>
              <div className={`${CommonClasses.flexBetween} mb-4`}>
                <div>
                  <h3
                    className={`font-semibold ${CommonClasses.textLg} ${CommonClasses.textGrayStrong}`}
                  >
                    {getCoinName(wallet.coin)}
                  </h3>
                  <p
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    {wallet.network.toUpperCase()} Network
                  </p>
                </div>
                {getStatusBadge(wallet.is_active)}
              </div>

              {/* Address */}
              <div className="mb-4">
                <label className={GlobalStyles.label}>Wallet Address</label>
                <div className="w-full flex gap-2">
                  <input
                    type="text"
                    value={wallet.address}
                    readOnly
                    className={`${GlobalStyles.input} bg-gray-50 dark:bg-gray-700 font-mono text-sm truncate`}
                  />
                  <button
                    onClick={() => handleCopyAddress(wallet.address)}
                    className={`${GlobalStyles.buttonIconOutline}`}
                  >
                    {copiedAddress === wallet.address ? (
                      <CheckIcon className="w-4 h-4 text-green-500" />
                    ) : (
                      <CopyIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* QR Code Placeholder */}
              {wallet.qr_code && (
                <div className="mb-4">
                  <label className={GlobalStyles.label}>QR Code</label>
                  <div className="bg-white p-2 rounded border border-gray-300 dark:border-gray-600">
                    <img
                      src={getDocumentUrl(wallet?.qr_code) || ""}
                      alt="QR Code"
                      className="w-full h-auto max-w-[200px] mx-auto"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(wallet)}
                  className={`${GlobalStyles.buttonOutline} flex-1 !text-xs`}
                >
                  <EditIcon className="w-3 h-3 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(wallet.id)}
                  className={`${GlobalStyles.buttonIconDanger}`}
                >
                  <DeleteIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Wallet Modal */}
      {isModalOpen && (
        <div className={GlobalStyles.modalOverlay}>
          <div className={GlobalStyles.modalContainer}>
            <div
              className={`p-5 border-b border-gray-300 dark:border-gray-700`}
            >
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
              >
                {editingWallet ? "Edit Wallet" : "Add New Wallet"}
              </h2>
              <p
                className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-1`}
              >
                {editingWallet
                  ? "Update wallet address details"
                  : "Configure new wallet address for deposits"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Coin Selection */}
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>Coin</label>
                <select
                  value={formData.coin}
                  onChange={(e) => {
                    const coin = coins.find((c) => c.id === e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      coin: e.target.value,
                      network: coin?.networks[0] || "bitcoin",
                    }));
                  }}
                  className={GlobalStyles.input}
                  required
                >
                  {coins.map((coin) => (
                    <option key={coin.id} value={coin.id}>
                      {coin.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Network Selection */}
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>Network</label>
                <select
                  value={formData.network}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      network: e.target.value,
                    }))
                  }
                  className={GlobalStyles.input}
                  required
                >
                  {coins
                    .find((coin) => coin.id === formData.coin)
                    ?.networks.map((network) => (
                      <option key={network} value={network}>
                        {network.toUpperCase()}
                      </option>
                    ))}
                </select>
              </div>

              {/* Wallet Address */}
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>Wallet Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                  placeholder="Enter wallet address"
                  className={GlobalStyles.input}
                  required
                />
              </div>

              {/* QR Code Upload */}
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>QR Code Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className={GlobalStyles.input}
                />
                {qrCodePreview && (
                  <div className="mt-2">
                    <p
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray} mb-2`}
                    >
                      Preview:
                    </p>
                    <img
                      src={qrCodePreview}
                      alt="QR Code Preview"
                      className="w-32 h-32 object-contain border border-gray-300 dark:border-gray-600 rounded"
                    />
                  </div>
                )}
                {editingWallet?.qr_code && !qrCodePreview && (
                  <div className="mt-2">
                    <p
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray} mb-2`}
                    >
                      Current QR Code:
                    </p>
                    <img
                      src={getDocumentUrl(editingWallet.qr_code) || ""}
                      alt="Current QR Code"
                      className="w-32 h-32 object-contain border border-gray-300 dark:border-gray-600 rounded"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray} ml-2`}
                >
                  Active
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingWallet(null);
                  }}
                  className={`${GlobalStyles.buttonOutline} flex-1`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${GlobalStyles.buttonPrimary} flex-1`}
                >
                  {editingWallet ? "Update Wallet" : "Add Wallet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletManagement;
